// Phase 6.3.3 (S1.2) — Tooltip primitive.
//
// Hover/focus affordance for usernames, timestamps, and the
// message-hover toolbar buttons. Appears after a hover delay (or
// immediately on keyboard focus), dismisses on leave/blur/Esc, and
// auto-flips when the chosen placement would clip the viewport.
// Visual matches the Popover surface (bg-elevated + shadow).

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type TooltipPlacement = "top" | "bottom" | "left" | "right";

export interface TooltipProps {
  content: ReactNode;
  placement?: TooltipPlacement;
  /** Hover delay in ms before showing (default 500). Focus is
   *  always immediate. */
  delay?: number;
  children: ReactNode;
}

const PLACEMENT_CLASS: Record<TooltipPlacement, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-tight",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-tight",
  left: "right-full top-1/2 -translate-y-1/2 mr-tight",
  right: "left-full top-1/2 -translate-y-1/2 ml-tight",
};

/** Flip the placement when the trigger sits too close to the
 *  corresponding viewport edge for the tooltip to fit. */
function resolvePlacement(base: TooltipPlacement, rect: DOMRect): TooltipPlacement {
  const margin = 48; // generous estimate of tooltip extent
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  if (base === "top" && rect.top < margin) return "bottom";
  if (base === "bottom" && vh - rect.bottom < margin) return "top";
  if (base === "left" && rect.left < margin) return "right";
  if (base === "right" && vw - rect.right < margin) return "left";
  return base;
}

export function Tooltip({
  content,
  placement = "top",
  delay = 500,
  children,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const [effective, setEffective] = useState<TooltipPlacement>(placement);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const tooltipId = useId();

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    const rect = wrapRef.current?.getBoundingClientRect();
    setEffective(rect ? resolvePlacement(placement, rect) : placement);
    setVisible(true);
  }, [placement]);

  const hide = useCallback(() => {
    clearTimer();
    setVisible(false);
  }, [clearTimer]);

  const onEnter = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(show, delay);
  }, [clearTimer, show, delay]);

  const onFocus = useCallback(() => {
    clearTimer();
    show();
  }, [clearTimer, show]);

  useEffect(() => clearTimer, [clearTimer]);

  return (
    <span
      ref={wrapRef}
      data-testid="tooltip-trigger"
      className="relative inline-flex"
      onMouseEnter={onEnter}
      onMouseLeave={hide}
      onFocus={onFocus}
      onBlur={hide}
      onKeyDown={(e) => {
        if (e.key === "Escape") hide();
      }}
      aria-describedby={visible ? tooltipId : undefined}
    >
      {children}
      {visible && (
        <span
          id={tooltipId}
          role="tooltip"
          data-testid="tooltip"
          data-placement={effective}
          className={[
            "absolute z-50 w-max max-w-[280px] pointer-events-none",
            "bg-theme-bg-elevated border border-theme-border-default shadow-2xl",
            "px-snug py-tight text-caption normal-case tracking-normal text-theme-text-primary",
            PLACEMENT_CLASS[effective],
          ].join(" ")}
        >
          {content}
        </span>
      )}
    </span>
  );
}
